import { ACHIEVEMENTS, type AchievementCategory } from '@/models/achievements';
import type { AchievementManager } from '@/systems/AchievementManager';

const CATEGORIES: AchievementCategory[] = ['building', 'production', 'prestige', 'raids', 'dedication'];

const CATEGORY_LABELS: Record<AchievementCategory, string> = {
  building: '🏗️ Building',
  production: '🪙 Production',
  prestige: '⬆️ Prestige',
  raids: '⚔️ Raids',
  dedication: '📅 Dedication',
};

export class AchievementPanel {
  private container: HTMLElement;
  private listEl: HTMLElement;
  private activeCategory: AchievementCategory = 'building';

  constructor(parent: HTMLElement) {
    this.container = document.createElement('div');
    this.container.id = 'achievement-panel';
    this.container.className = 'game-panel';

    // Header
    const header = document.createElement('div');
    header.className = 'game-panel-header';
    header.innerHTML = `
      <div class="game-panel-title">ACHIEVEMENTS</div>
      <button class="game-panel-close">&times;</button>
    `;
    header.querySelector('.game-panel-close')!.addEventListener('click', () => this.hide());
    this.container.appendChild(header);

    // Category tabs
    const tabs = document.createElement('div');
    tabs.className = 'achievement-tabs';
    for (const cat of CATEGORIES) {
      const tab = document.createElement('button');
      tab.className = 'achievement-tab' + (cat === this.activeCategory ? ' active' : '');
      tab.dataset['category'] = cat;
      tab.textContent = CATEGORY_LABELS[cat];
      tab.addEventListener('click', () => {
        this.activeCategory = cat;
        tabs.querySelectorAll('.achievement-tab').forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
        this.renderList();
      });
      tabs.appendChild(tab);
    }
    this.container.appendChild(tabs);

    // Achievement list
    this.listEl = document.createElement('div');
    this.listEl.className = 'achievement-list';
    this.container.appendChild(this.listEl);

    parent.appendChild(this.container);
  }

  update(manager: AchievementManager): void {
    this._manager = manager;
    this.renderList();
  }

  private _manager: AchievementManager | null = null;

  private renderList(): void {
    if (!this._manager) return;
    const manager = this._manager;

    this.listEl.innerHTML = '';
    const filtered = ACHIEVEMENTS.filter(a => a.category === this.activeCategory);

    for (const a of filtered) {
      const unlocked = manager.isUnlocked(a.id);
      const progress = manager.getProgress(a.id);

      const item = document.createElement('div');
      item.className = 'achievement-item' + (unlocked ? ' unlocked' : '');

      if (unlocked) {
        item.innerHTML = `
          <div class="achievement-item-header">
            <span class="achievement-item-icon">${a.icon}</span>
            <div class="achievement-item-info">
              <div class="achievement-item-name">${a.name}</div>
              <div class="achievement-item-desc">${a.description}</div>
            </div>
            <span class="achievement-item-check">✓</span>
          </div>
          <div class="achievement-item-reward">+${a.reward} 💎</div>
        `;
      } else {
        const pct = Math.round(progress.percent * 100);
        item.innerHTML = `
          <div class="achievement-item-header">
            <span class="achievement-item-icon locked">${a.icon}</span>
            <div class="achievement-item-info">
              <div class="achievement-item-name">${a.name}</div>
              <div class="achievement-item-desc">${a.description}</div>
            </div>
            <span class="achievement-item-reward-small">+${a.reward} 💎</span>
          </div>
          <div class="progress-bar" style="margin-top: 6px;">
            <div class="progress-fill" style="width: ${pct}%;"></div>
          </div>
          <div class="achievement-item-progress">${progress.current} / ${progress.target}</div>
        `;
      }

      this.listEl.appendChild(item);
    }
  }

  show(): void {
    this.container.style.display = 'block';
    this.renderList();
  }

  hide(): void {
    this.container.style.display = 'none';
  }

  toggle(): void {
    if (this.container.style.display === 'none') {
      this.show();
    } else {
      this.hide();
    }
  }

  destroy(): void {
    this.container.remove();
  }
}
